import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, Mail, Loader2, Eye, EyeOff, Phone } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Force clear stale caches + aggressive SW refresh on login page
  useEffect(() => {
    (async () => {
      try {
        if ('caches' in window) {
          const names = await caches.keys();
          // Delete ALL non-current caches (v1, v2, v3, v4, old)
          await Promise.all(names.filter(n => n !== 'aisha-crm-v5' && n !== 'aisha-static-v5').map(n => caches.delete(n)));
        }
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          // If we have an outdated SW (no active or wrong scope), unregister and reload once
          const hasOld = regs.some(r => r.active && !r.active.scriptURL.includes('/sw.js'));
          if (hasOld) {
            await Promise.all(regs.map(r => r.unregister()));
            if (!sessionStorage.getItem('sw_reset_done')) {
              sessionStorage.setItem('sw_reset_done', '1');
              window.location.reload();
              return;
            }
          }
          regs.forEach(r => r.update());
        }
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Wprowadź email i hasło');
      return;
    }
    setLoading(true);
    try {
      const result = await login(email, password);
      // If 2FA required, navigate to /2fa with partial token + methods
      if (result?.requires_2fa) {
        navigate('/2fa', { state: result });
        return;
      }
      toast.success('Witamy w Aisha Travel CRM!');
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      const msg = error.response?.data?.detail || error.message || 'Nieprawidłowe dane logowania';
      if (error.response?.status === 429) {
        toast.error('Zbyt wiele prób. Odczekaj 5 minut.');
      } else if (error.response?.status === 403) {
        toast.error('Konto zablokowane. Skontaktuj się z administratorem.');
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0B1F3A 0%, #1A3456 50%, #0B1F3A 100%)' }}>
      <div className="w-full max-w-md animate-fade-in flex-1 flex items-center justify-center">
        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-xl w-full">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-20 h-20 flex items-center justify-center mb-4">
              <img src="/logo.png" alt="Aisha Travel" className="w-20 h-20 object-contain drop-shadow-lg" />
            </div>
            <CardTitle className="text-2xl font-bold text-navy-800 font-heading">Aisha Travel CRM</CardTitle>
            <CardDescription className="text-muted-foreground">
              Zaloguj się do systemu zarządzania
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-navy-700 font-medium">Email lub login</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email" type="text" placeholder="email@firma.pl lub login"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11 border-slate-200 focus:ring-2 focus:ring-gold-400/30 focus:border-gold-500"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-navy-700 font-medium">Hasło</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password" 
                    type={showPassword ? "text" : "password"} 
                    placeholder="••••••••"
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-11 border-slate-200 focus:ring-2 focus:ring-gold-400/30 focus:border-gold-500"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => navigate('/zapomnialem-hasla')}
                    className="text-xs text-navy-600 hover:text-gold-600 hover:underline transition-colors"
                  >
                    Zapomniałem hasła
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 bg-gold-500 hover:bg-gold-600 text-navy-800 font-semibold shadow-lg shadow-gold-400/20 transition-all duration-200 active:scale-[0.98]"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Logowanie...</>
                ) : 'Zaloguj się'}
              </Button>
            </form>
            
            {/* Contact info */}
            <div className="mt-5 pt-4 border-t border-slate-100">
              <p className="text-xs text-center text-slate-500 flex items-center justify-center gap-1">
                <Phone className="w-3 h-3" />
                Problemy z logowaniem? Skontaktuj się z administratorem: 
                <a href="tel:+48733000136" className="text-navy-600 hover:text-gold-600 font-medium">+48 733 000 136</a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Footer */}
      <footer className="py-4 text-center">
        <p className="text-xs text-white/60">
          Aisha Travel CRM &copy; 2026
        </p>
      </footer>
    </div>
  );
};

export default LoginPage;
